import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { Field, Select, TextArea, TextInput } from '@/components/Field'
import { Kicker } from '@/components/Kicker'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { supabase } from '@/lib/supabase'
import type { NeedCategory, NeedRecurrence, NeedUrgency } from '@/lib/types'
import { PACK_COLORS, packForNeedCategory } from '@/design-system/tokens'
import { emitEvent } from '@/lib/emitEvent'
import {
  NEED_PRESETS,
  computeDueAt,
  guessCategoryFromTitle,
  recurrenceLabel,
  type NeedPreset,
} from '@/lib/needPresets'
import { useToasts } from '@/lib/toast'

const CATEGORIES: NeedCategory[] = [
  'feeding', 'clothing', 'equipment', 'document', 'appointment',
  'education', 'activity', 'health', 'financial', 'logistics',
  'safety', 'maintenance', 'other',
]
const URGENCIES: NeedUrgency[] = ['low', 'normal', 'high', 'critical']
const RECURRENCES: Array<{ value: '' | NeedRecurrence; label: string }> = [
  { value: '',         label: "Doesn't repeat" },
  { value: 'daily',    label: 'Every day' },
  { value: 'weekdays', label: 'Every weekday (Mon–Fri)' },
  { value: 'weekends', label: 'Every weekend (Sat–Sun)' },
  { value: 'weekly',   label: 'Every week' },
  { value: 'monthly',  label: 'Every month' },
]

function toLocalInputValue(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function NewNeedScreen() {
  const nav = useNavigate()
  const familyId = useAppStore((s) => s.familyId)
  const user = useAppStore((s) => s.user)
  const focusedPerson = useFocusedPerson()
  const persons = useAppStore((s) => s.persons)
  const qc = useQueryClient()
  const pushToast = useToasts((s) => s.push)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<NeedCategory>('other')
  const [categoryUserSet, setCategoryUserSet] = useState(false)  // stop autoguessing once user picks
  const [urgency, setUrgency] = useState<NeedUrgency>('normal')
  const [personId, setPersonId] = useState<string | ''>(focusedPerson?.id ?? '')
  const [dueAt, setDueAt] = useState('')
  const [recurrence, setRecurrence] = useState<'' | NeedRecurrence>('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Auto-detect category from title as the parent types. Only overrides
  // the default 'other' until the user manually picks a category.
  useEffect(() => {
    if (categoryUserSet) return
    const guess = guessCategoryFromTitle(title)
    if (guess && guess !== category) setCategory(guess)
  }, [title, categoryUserSet, category])

  function applyPreset(p: NeedPreset) {
    setTitle(p.title)
    setCategory(p.category)
    setCategoryUserSet(true)  // preset counts as an explicit choice
    setUrgency(p.urgency)
    setRecurrence(p.recurrence ?? '')
    const due = computeDueAt(p)
    setDueAt(due ? toLocalInputValue(due) : '')
    // Person defaults to focused unless preset targets whole family.
    if (!personId && focusedPerson?.id) setPersonId(focusedPerson.id)
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!familyId || !user) return
    setBusy(true); setErr(null)
    try {
      if (!title.trim()) throw new Error('Title is required')
      const dueIso = dueAt ? new Date(dueAt).toISOString() : null
      const { data: needRow, error } = await supabase
        .from('needs')
        .insert({
          family_id: familyId,
          person_id: personId || null,
          title: title.trim(),
          description: description.trim() || null,
          category,
          urgency,
          source_type: 'parent',
          confidence: 1.0,
          due_at: dueIso,
          recurrence: recurrence || null,
          created_by_user_id: user.id,
        })
        .select('id')
        .single()
      if (error) throw new Error(error.message)

      await emitEvent({
        familyId,
        personId: personId || null,
        userId: user.id,
        eventType: 'need.created',
        payload: {
          need_id: needRow!.id,
          title: title.trim(),
          category,
          urgency,
          source_type: 'parent',
          confidence: 1.0,
          ...(recurrence ? { recurrence } : {}),
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(dueIso ? { due_at: dueIso } : {}),
        } as import('@/lib/types').JSONValue,
      })

      await qc.invalidateQueries({ queryKey: ['needs'] })
      pushToast({
        title: recurrence ? 'Recurring need added' : 'Need added',
        body: recurrence ? `${title.trim()} · ${recurrenceLabel(recurrence)}` : title.trim(),
        tone: 'mint',
        ttl: 2500,
      })
      nav('/needs', { replace: true })
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const color = PACK_COLORS[packForNeedCategory(category)].light

  return (
    <Screen title="New need" back>
      <form onSubmit={save} className="px-5 py-5 space-y-5">
        <div>
          <Kicker>Common</Kicker>
          <div className="mt-2 flex flex-wrap gap-2">
            {NEED_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p)}
                className="inline-flex items-center gap-1.5 rounded-full border border-divider-subtle bg-bg-elevated px-3 py-1.5 text-caption text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors"
              >
                <span aria-hidden>{p.emoji}</span>
                <span>{p.label}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-tag text-text-quiet">Tap to prefill title, category, urgency, and typical time.</p>
        </div>

        <Kicker color={color}>Manual need</Kicker>

        <Field label="Title">
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What needs to happen?"
            required
            autoFocus
          />
        </Field>

        <Field label="Description (optional)">
          <TextArea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <Select value={category} onChange={(e) => { setCategory(e.target.value as NeedCategory); setCategoryUserSet(true) }}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </Field>
          <Field label="Urgency">
            <Select value={urgency} onChange={(e) => setUrgency(e.target.value as NeedUrgency)}>
              {URGENCIES.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Person (optional)">
          <Select value={personId} onChange={(e) => setPersonId(e.target.value)}>
            <option value="">Family (no specific person)</option>
            {persons.map((p) => (
              <option key={p.id} value={p.id}>{p.display_name}</option>
            ))}
          </Select>
        </Field>

        <Field label="Due (optional)">
          <TextInput type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
        </Field>

        <Field label="Repeats">
          <Select value={recurrence} onChange={(e) => setRecurrence(e.target.value as '' | NeedRecurrence)}>
            {RECURRENCES.map((r) => (
              <option key={r.value || 'none'} value={r.value}>{r.label}</option>
            ))}
          </Select>
        </Field>
        {recurrence && (
          <Chip color={color} emphasis="quiet">
            Repeats: {recurrenceLabel(recurrence)}
          </Chip>
        )}

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" packColor={color} loading={busy} className="w-full">
          Create need
        </Button>
      </form>
    </Screen>
  )
}
